<template>
    <v-form ref="form" v-model="valid" data-cy="point-form">
        <v-row class="mb-4 flex-wrap" dense>
            <v-col>
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col cols="12" md="6">
                <v-autocomplete
                    ref="option"
                    v-model="editablePoint.countryId"
                    :items="useCountries"
                    item-title="title"
                    item-value="value"
                    :label="`${$t('country')}*`"
                    closable-chips
                    hide-details
                    clearable
                    density="compact"
                    color="primary"
                    data-cy="country-id"
                    class="mb-4"
                    :rules="[requiredRule('country')]"
                />
            </v-col>
            <v-col cols="12" md="6">
                <v-autocomplete
                    ref="cityRef"
                    v-model="editablePoint.cityId"
                    :items="cities"
                    item-title="title"
                    item-value="value"
                    :label="`${$t('city')}*`"
                    :loading="isCitiesLoading"
                    :disabled="isCitiesLoading || !cities.length"
                    closable-chips
                    hide-details
                    clearable
                    density="compact"
                    color="primary"
                    data-cy="city-id"
                    class="mb-4"
                    :rules="[requiredRule('cities')]"
                />
            </v-col>
        </v-row>
        <v-row dense>
            <v-col>
                <div class="text-subtitle-1 text-capitalize pb-2">
                    {{ pointType === 'departure' ? $t('from') : $t('to') }}:
                </div>
                <v-select
                    v-model="editablePoint.type"
                    :items="useContractLocationType"
                    density="compact"
                    variant="outlined"
                    color="primary"
                    hide-details
                    class="mb-4"
                    data-cy="type"
                    @update:model-value="resetLocationCode()"
                />
                <template v-if="isTransferForm">
                    <v-autocomplete
                        v-if="editablePoint.type === StartEndLocation.Airport"
                        v-model="editablePoint.locationCode"
                        :items="airports"
                        :label="`${$t('airport')}`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="airports"
                        clearable
                        @update:model-value="e => handleSelection(e, editablePoint.type)"
                    >
                        <template v-slot:item="{item, props}">
                            <v-list-item v-bind="props">
                                <template v-slot:title>
                                    <span v-text="`${item.raw.title} (${item.raw.value})`" />
                                </template>
                            </v-list-item>
                        </template>
                        <template v-slot:selection="{item}">
                            {{ `${item.raw.title} (${item.raw.value})` }}
                        </template>
                    </v-autocomplete>
                    <v-autocomplete
                        v-if="editablePoint.type === StartEndLocation.Station"
                        v-model="editablePoint.locationCode"
                        :items="stations"
                        :label="`${$t('station')}`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="station"
                        clearable
                        @update:model-value="e => handleSelection(e, editablePoint.type)"
                    />
                    <v-autocomplete
                        v-if="editablePoint.type === StartEndLocation.Port"
                        v-model="editablePoint.locationCode"
                        :items="ports"
                        :label="`${$t('cruises.port')}`"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="port"
                        clearable
                        @update:model-value="e => handleSelection(e, editablePoint.type)"
                    />
                </template>
            </v-col>
        </v-row>
        <v-row v-if="editablePoint.type === StartEndLocation.Manual" dense>
            <v-col>
                <TextFieldLocale
                    v-model="editablePoint.name[activeLangCode]"
                    :label="$t('name')"
                    :active-lang-code="activeLangCode"
                    data-cy="name"
                />
            </v-col>
        </v-row>
        <v-row v-if="editablePoint.type === StartEndLocation.Manual" dense class="pb-6">
            <v-col>
                <v-text-field
                    v-model="editablePoint.latitude"
                    :label="$t('latitude')"
                    data-cy="latitude"
                    hide-details
                    variant="outlined"
                    color="primary"
                    density="compact"
                />
            </v-col>
            <v-col>
                <v-text-field
                    v-model="editablePoint.longitude"
                    :label="$t('longitude')"
                    data-cy="longitude"
                    hide-details
                    variant="outlined"
                    color="primary"
                    density="compact"
                />
            </v-col>
        </v-row>
        <v-row dense class="align-center">
            <div class="text-subtitle-1">{{ $t('time_shift_note') }}:</div>
            <v-col cols="6" sm="3">
                <v-text-field
                    v-model="editablePoint.timeShift"
                    label="(HH:mm)"
                    :rules="[timeFormatRule()]"
                    variant="outlined"
                    color="primary"
                    density="compact"
                    data-cy="time-shift"
                    maxlength="5"
                    hide-details
                    @update:model-value="e => formatTime(e, 'timeShift')"
                />
            </v-col>
        </v-row>
    </v-form>
</template>
<script setup lang="ts">
    import {requiredRule, timeFormatRule} from '~/composables/rules'
    import TextFieldLocale from '~/components/TextFieldLocale.vue'
    import {useCountries} from '~/composables/form-helpers'
    import {contractServiceType, type SelectOptionType, StartEndLocation} from '~/utils/types/types'

    const props = defineProps<{
        pointType: string
        serviceType: string
    }>()

    const valid = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    const editablePoint = defineModel<any>()
    const isCitiesLoading = ref<boolean>(false)
    const cities = ref<SelectOptionType[]>([])
    const cityRef = ref()
    const isTransferForm = ref<boolean>(props.serviceType === contractServiceType.ownTransfer)

    watch(
        () => editablePoint.value.countryId,
        async (): Promise<void> => {
            if (editablePoint.value.cityId) cityRef.value.reset()
            editablePoint.value.cityName = null
            cities.value = []
            editablePoint.value.type = 'HOTEL'
            if (isTransferForm.value) resetForm()

            if (editablePoint.value.countryId) {
                await loadCities()
                const selectedCountry = useCountries.value.find(
                    country => country.value === editablePoint.value.countryId
                )
                editablePoint.value.countryName = selectedCountry.title || ''
            }
        },
        {deep: true}
    )

    watch(
        () => editablePoint.value.cityId,
        async (): Promise<void> => {
            editablePoint.value.type = 'HOTEL'
            if (editablePoint.value.cityId) {
                const index = cities.value.findIndex(item => item.value === editablePoint.value.cityId)
                if (index !== -1) {
                    editablePoint.value.cityName = cities.value[index].title
                } else {
                    editablePoint.value.cityName = ''
                }
                if (isTransferForm.value) {
                    resetForm()
                    await Promise.all([loadAirports(), loadStations(), loadPorts()])
                }
            }
        },
        {deep: true}
    )

    const resetForm = () => {
        editablePoint.value.locationCode = null
        airports.value = []
        stations.value = []
        ports.value = []
    }

    const resetLocationCode = () => {
        if (editablePoint.value?.locationCode && isTransferForm.value) {
            editablePoint.value.locationCode = null
        }
    }

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        if (editablePoint.value.cityId) {
            if (props.serviceType === contractServiceType.ownExcursion) await loadCities()
            if (isTransferForm.value) {
                await Promise.all([loadCities(), loadAirports(), loadStations(), loadPorts()])
            }
        }
    })

    const isAirportsLoading = ref<boolean>(true)
    const airports = ref<SelectOptionType[]>([])
    async function loadAirports(): Promise<void> {
        try {
            isAirportsLoading.value = true
            const rs = await app.$api.airports.get({
                orderBy: 'ALPHABET',
                locationId: editablePoint.value.cityId,
            })
            if (rs.length) {
                airports.value = rs.map(({iataCode, name}) => ({title: `${name}`, value: iataCode}))
            }
        } catch (e) {
            airports.value = []
        } finally {
            isAirportsLoading.value = false
        }
    }

    const isStationsLoading = ref<boolean>(true)
    const stations = ref<SelectOptionType[]>([])
    async function loadStations(): Promise<void> {
        try {
            isStationsLoading.value = true
            const rs = await app.$api.trainStations.get({
                orderBy: 'ALPHABET',
                productType: 'TRANSFER',
                locationId: editablePoint.value.cityId,
            })
            if (rs.length) {
                stations.value = rs.map(({code, name}) => ({title: name, value: code}))
            }
        } catch (e) {
            stations.value = []
        } finally {
            isStationsLoading.value = false
        }
    }

    const isPortsLoading = ref<boolean>(true)
    const ports = ref<SelectOptionType[]>([])
    async function loadPorts(): Promise<void> {
        try {
            isPortsLoading.value = true
            const rs = await app.$api.cruisePorts.get({
                orderBy: 'ALPHABET',
                locationId: editablePoint.value.cityId,
            })
            if (rs.length) {
                ports.value = rs.map(({code, name}) => ({title: name, value: code}))
            }
        } catch (e) {
            ports.value = []
        } finally {
            isPortsLoading.value = false
        }
    }

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
    }

    const formatTime = (event, key): void => {
        const filteredValue = event.replace(/[^0-9:]/g, '')
        const formattedValue = filteredValue.replace(/(\d{2})(?=\d)/g, '$1:')
        editablePoint.value[key] = formattedValue
    }

    const useContractLocationType = computed(() => {
        enum Locations {
            'AIRPORT' = 1,
            'STATION' = 2,
            'PORT' = 3,
            'ADDRESS' = 4,
            'HOTEL' = 5,
            'MANUAL' = 6,
        }
        let contractLocationType = [StartEndLocation.Hotel, StartEndLocation.Manual]
        if (airports.value.length) {
            contractLocationType = [StartEndLocation.Airport, ...contractLocationType]
        }
        if (stations.value.length) {
            contractLocationType = [StartEndLocation.Station, ...contractLocationType]
        }
        if (ports.value.length) {
            contractLocationType = [StartEndLocation.Port, ...contractLocationType]
        }

        return contractLocationType
            .sort((a, b) => Locations[a] - Locations[b])
            .map(value => ({value, title: t(`transfers.transferPlaceType.${value}`)}))
    })

    async function loadCities() {
        try {
            isCitiesLoading.value = true
            const rs = await app.$api.locations.get({
                limitCities: -1,
                orderBy: 'POPULARITY',
                countryId: editablePoint.value.countryId,
            })
            if (rs.cities) {
                cities.value = rs.cities.map(({id, name}) => {
                    return {title: name, value: id}
                })
            }
        } catch (e) {
            cities.value = []
        } finally {
            isCitiesLoading.value = false
        }
    }

    const handleSelection = (value, type) => {
        let selectedItem
        switch (type) {
            case StartEndLocation.Airport:
                selectedItem = airports.value.find(airport => airport.value === value)
                break
            case StartEndLocation.Station:
                selectedItem = stations.value.find(airport => airport.value === value)
                break
            case StartEndLocation.Port:
                selectedItem = ports.value.find(airport => airport.value === value)
                break
        }
        editablePoint.value.locationName = selectedItem.title
    }

    defineExpose({
        valid,
    })
</script>
